import {
  DeviceLabels,
  useMeetingManager
} from "amazon-chime-sdk-component-library-react";
import {
  ConsoleLogger,
  DefaultEventController,
  LogLevel,
  MeetingSessionConfiguration,
  NoOpEventReporter
} from "amazon-chime-sdk-js";
import { useEffect, useRef } from "react";
import { AttendeeInfo, MeetingInfo } from "../types/meeting.types";
import { stopAllMediaTracks } from "../utils/mediaUtils";

interface UseMeetingSetupProps {
  meetingResponse: MeetingInfo;
  attendeeResponse: AttendeeInfo;
}

export const useMeetingSetup = ({
  meetingResponse,
  attendeeResponse
}: UseMeetingSetupProps) => {
  const meetingManager = useMeetingManager();
  const audioRef = useRef<HTMLAudioElement>(null);

  /**
   * Holding a reference to the media stream that we request up-front for permissions
   * allows us to explicitly stop its tracks later. If we don’t keep the reference
   * we lose the ability to stop the original tracks, which can leave the camera
   * and microphone showing as “in-use” even after the meeting ends.
   */
  const permissionStreamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    async function configMeeting() {
      const meetingSessionConfiguration = new MeetingSessionConfiguration(
        meetingResponse,
        attendeeResponse
      );

      try {
        // First, request media permissions before joining and keep a reference
        // to the stream so we can properly stop it during cleanup.
        permissionStreamRef.current = await navigator.mediaDevices.getUserMedia(
          {
            audio: true,
            video: true
          }
        );

        const eventController = new DefaultEventController(
          meetingSessionConfiguration,
          new ConsoleLogger("SDK", LogLevel.WARN),
          new NoOpEventReporter()
        );

        // Join the meeting
        await meetingManager.join(meetingSessionConfiguration, {
          deviceLabels: DeviceLabels.AudioAndVideo,
          eventController
        });

        // Wait a bit for the join to complete properly
        await new Promise(resolve => setTimeout(resolve, 500));

        // Start the meeting session
        await meetingManager.start();

        // Wait for the meeting to be fully started
        await new Promise(resolve => setTimeout(resolve, 1000));

        if (meetingManager.audioVideo) {
          // Set up audio permissions
          meetingManager.audioVideo.realtimeSetCanUnmuteLocalAudio(true);

          // Start video tile
          meetingManager.audioVideo.startLocalVideoTile();

          // Stop any content sharing
          meetingManager.audioVideo.stopContentShare();
        }

        console.log("Meeting setup completed successfully");
      } catch (error: any) {
        console.error("Meeting setup error:", error.message);
        // Uncomment if you want to show toast errors
        // toast.error(`${error.message}`, { toastId: 'meeting-error' });
      }
    }

    configMeeting();

    return () => {
      // Cleanup function – be more careful about cleanup order

      // 1. Stop the initial permission stream if it exists
      if (permissionStreamRef.current) {
        try {
          permissionStreamRef.current
            .getTracks()
            .forEach(track => track.stop());
        } catch (err) {
          console.warn("Error stopping permission stream tracks:", err);
        }
        permissionStreamRef.current = null;
      }

      if (meetingManager.audioVideo) {
        try {
          meetingManager.audioVideo.stopLocalVideoTile();
          meetingManager.audioVideo.stopContentShare();

          if (audioRef.current) {
            meetingManager.audioVideo.unbindAudioElement();
          }

          const tileIds = meetingManager.audioVideo
            .getAllVideoTiles()
            .map(tile => tile.state().tileId);
          tileIds.forEach(tileId => {
            if (tileId) meetingManager.audioVideo?.unbindVideoElement(tileId);
          });
        } catch (error) {
          console.error("Cleanup error:", error);
        }
      }

      // Attempt to fully release audio and video devices
      if (meetingManager.audioVideo) {
        try {
          const avAny = meetingManager.audioVideo as any;
          if (typeof avAny.chooseVideoInputDevice === "function") {
            avAny.chooseVideoInputDevice(null);
          }
          if (typeof avAny.chooseAudioInputDevice === "function") {
            avAny.chooseAudioInputDevice(null);
          }
        } catch (deviceReleaseError) {
          console.warn("Device release error:", deviceReleaseError);
        }
      }

      stopAllMediaTracks();

      if (meetingManager) {
        try {
          meetingManager.leave();
        } catch (error) {
          console.error("Leave meeting error:", error);
        }
      }
    };
  }, [meetingResponse, attendeeResponse, meetingManager]);

  // Bind audio element
  useEffect(() => {
    if (audioRef.current && meetingManager.audioVideo) {
      meetingManager.audioVideo.bindAudioElement(audioRef.current);
    }
  }, [meetingManager, audioRef]);

  return { audioRef };
};
